import { useCallback, useEffect, useState } from 'react';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { useSessionStore } from '../../../sessions/store/sessionStore';
import { resolveEngineIdForTab } from '../utils/resolveEngineId';
import type { DatabaseEngineId } from '../../../../core/engines';

type UseTabConnectionStateParams = {
  tabId: string;
  connectionId: string | undefined;
  /** Whether the tab carries connection info at all (drives the lost-connection check). */
  hasConnectionInfo: boolean;
};

/**
 * Connection/session state for a query editor tab: engine resolution, the
 * session-store-driven disconnected detection (what the tab restores against
 * after a session restore or a dropped connection), the disconnected-banner
 * dismissal state, the reconnect attempt, and the connection-lost handler used
 * by query execution. Behavior carried verbatim from the previous inline
 * implementation.
 */
export function useTabConnectionState({
  tabId,
  connectionId,
  hasConnectionInfo,
}: UseTabConnectionStateParams) {
  // Get the engine type from the active connection
  const activeConnections = useConnectionStore((state) => state.activeConnections);
  const savedConnections = useConnectionStore((state) => state.connections);
  const connectToDatabase = useConnectionStore((state) => state.connectToDatabase);
  const markConnectionDisconnected = useConnectionStore((state) => state.markConnectionDisconnected);
  const activeConnection = activeConnections.find((conn) => conn.id === connectionId);
  // `activeConnections` only gains this entry once `connectToDatabase`
  // resolves (a known e2e race, `reference_e2e_active_connections`, that also
  // affects real renders during a tab's brief pre-connect window). Fall back
  // to the persisted `savedConnections` record (known independent of the
  // connect flow) before defaulting to 'mssql', so inline DDL pickers
  // (VqeDdlEdit Slice A1/B*/C*) don't briefly mis-gate on the wrong engine.
  const engineId: DatabaseEngineId = resolveEngineIdForTab(
    activeConnection?.connection?.engine,
    connectionId ? savedConnections.find((c) => c.id === connectionId)?.engine : undefined,
  );
  const connectionColor = connectionId ? savedConnections.find((c) => c.id === connectionId)?.color : undefined;

  // Check if connection is disconnected based on session state OR activeConnection status
  const currentSession = useSessionStore((state) => state.currentSession);
  const updateConnectionStatus = useSessionStore((state) => state.updateConnectionStatus);
  const sessionConnectionStatus = connectionId
    ? currentSession?.connections.find((c) => c.connectionId === connectionId)?.status
    : undefined;
  // Connection is disconnected if:
  // 1. Session explicitly marks it as 'disconnected', OR
  // 2. ActiveConnection has status 'disconnected', OR
  // 3. Tab has a connectionId but it's not in activeConnections (connection was lost)
  const isInActiveConnections = connectionId
    ? activeConnections.some((c) => c.id === connectionId)
    : false;
  const activeConnectionStatus = activeConnection?.status;
  const isDisconnected = connectionId
    ? sessionConnectionStatus === 'disconnected' || activeConnectionStatus === 'disconnected' || (!isInActiveConnections && hasConnectionInfo)
    : false;

  // Banner dismissal state (for this session only)
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Reset banner when tab changes or connection changes
  useEffect(() => {
    setBannerDismissed(false);
  }, [tabId, connectionId]);

  // Handle reconnection attempt
  const handleReconnect = useCallback(async (): Promise<boolean> => {
    if (!connectionId) return false;

    // Find the saved connection config
    const connection = savedConnections.find((c) => c.id === connectionId);
    if (!connection) return false;

    // Attempt to reconnect
    const success = await connectToDatabase(connection);
    if (success) {
      // Update session connection status
      updateConnectionStatus(connectionId, 'connected');
      setBannerDismissed(false);
    } else {
      // Ensure connection stays marked as disconnected on failure
      markConnectionDisconnected(connectionId);
      updateConnectionStatus(connectionId, 'disconnected');
    }
    return success;
  }, [connectionId, savedConnections, connectToDatabase, updateConnectionStatus, markConnectionDisconnected]);

  // Handle connection lost during query execution
  const handleConnectionLost = useCallback(() => {
    if (connectionId) {
      // Mark as disconnected in session store
      updateConnectionStatus(connectionId, 'disconnected');
      // Mark connection as disconnected (keeps it in activeConnections so explorer can update)
      markConnectionDisconnected(connectionId);
    }
  }, [connectionId, updateConnectionStatus, markConnectionDisconnected]);

  return {
    engineId,
    connectionColor,
    activeConnection,
    isDisconnected,
    bannerDismissed,
    setBannerDismissed,
    handleReconnect,
    handleConnectionLost,
  };
}
